import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const VerifyCode = () => {
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || ''); // Retrieve email from state
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendMessage, setResendMessage] = useState(''); // Message for resend status
  const [resendDisabled, setResendDisabled] = useState(false); // Disable resend button
  const [timer, setTimer] = useState(0); // Countdown timer
  const navigate = useNavigate();

  useEffect(() => {
    let countdown;
    if (resendDisabled && timer > 0) {
      countdown = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setResendDisabled(false);
    }
    return () => clearInterval(countdown);
  }, [resendDisabled, timer]);

  const handleVerify = async (e) => {
    e.preventDefault();

    if (code.length !== 5) {
      setError('Please enter a 5-digit code');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('https://panel.masit.net/api/auth/verfiycode.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          users_email: email.trim(), // Match the API's expected key
          users_verfiycode: code.trim(), // Match the API's expected key
        }),
      });

      const data = await response.json();

      if (data.status?.includes("success")) {
// Display a Tailwind CSS alert box
        const alertBox = document.createElement('div');
        alertBox.className = 'fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-green-500 text-white p-4 rounded shadow-lg';
        alertBox.textContent = 'Verification successful!';
        document.body.appendChild(alertBox);

        setTimeout(() => {
          alertBox.remove();
        }, 3000);

        navigate('/signin');
      } else {
        setError(data.status || 'Verification failed');
      }
    } catch (error) {
      setError('An error occurred: ' + error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    setResendMessage(''); // Clear previous message
    setIsSubmitting(true);
    setResendDisabled(true); // Disable the button
    setTimer(59); // Start the countdown

    try {
      const response = await fetch('https://panel.masit.net/api/auth/resend.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(), // Match the API's expected key
        }),
      });

      const data = await response.json();

      if (data.status?.includes("success")) {
        setResendMessage('A new verification code has been sent to your email.');
      } else {
        setResendMessage(data.status || 'Failed to resend the code.');
      }
    } catch (error) {
      setResendMessage('An error occurred while resending the code.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Verify Code</h2>
        <form onSubmit={handleVerify}>
          <div className="mb-4">
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500"
              required
              disabled
              hidden
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 dark:text-gray-300 mb-2" htmlFor="code">Verification Code</label>
            <input
              type="text"
              id="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500"
              required
              maxLength="5"
            />
          </div>
          {error && <p className="text-red-500 mb-4">{error}</p>}
          <button
            type="submit"
            className="w-full bg-red-600 hover:bg-red-500 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
            disabled={isSubmitting}
          >
            Verify
          </button>
        </form>
        <p className="mt-4 text-gray-600 dark:text-gray-400">
          Didn&apos;t receive a code?{' '}
          <button
            onClick={handleResendCode}
            className={`text-red-600 hover:text-red-500 underline ${resendDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={resendDisabled || isSubmitting}
          >
            {resendDisabled ? `Resend in ${timer}s` : 'Resend Code'}
          </button>
        </p>
        {resendMessage && <p className="mt-4 text-green-500">{resendMessage}</p>}
      </div>
    </div>
  );
};

export default VerifyCode;